import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalProduct.module.sass";
import Icon from "../Icon";
import Dropdown from "../Dropdown";
import Card from "../Card";
import { showSuccess, showError } from "../../services/toast.service";
import { getOpcoOptions } from "../../services/opco.service";
import { updateDraftFolder } from "../../services/folder.service";

const ModalChangeOpco = ({ visible, onClose, folderId, currentOpco, onOpcoUpdated }) => {
  const [selectedOpco, setSelectedOpco] = useState(currentOpco || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const [opcoOptions, setOpcoOptions] = useState([]);
  const [isLoadingOpcos, setIsLoadingOpcos] = useState(false);

  // Charger les options OPCO depuis PocketBase
  useEffect(() => {
    const loadOpcoOptions = async () => {
      if (visible) {
        setIsLoadingOpcos(true);
        try {
          const options = await getOpcoOptions();
          setOpcoOptions(options);
        } catch (error) {
          console.error('Erreur lors du chargement des OPCO:', error);
          showError("Erreur lors du chargement des OPCO");
        } finally {
          setIsLoadingOpcos(false);
        }
      }
    };

    loadOpcoOptions();
  }, [visible]);

  // Réinitialiser l'OPCO sélectionné quand la modal s'ouvre
  useEffect(() => {
    if (visible) {
      setSelectedOpco(currentOpco || "");
    }
  }, [visible, currentOpco]);

  const escFunction = useCallback(
    (e) => {
      if (e.keyCode === 27) {
        onClose();
      }
    },
    [onClose]
  );

  const handleSaveOpco = async () => {
    if (!selectedOpco) {
      showError("Veuillez sélectionner un OPCO");
      return;
    }

    if (!folderId) {
      showError("ID du dossier manquant");
      return;
    }
    
    try {
      setIsUpdating(true);
      
      // Mettre à jour le dossier avec le nouvel OPCO
      await updateDraftFolder(folderId, {
        opco_id: selectedOpco
      });

      showSuccess("OPCO mis à jour avec succès");
      
      if (typeof onOpcoUpdated === 'function') {
        onOpcoUpdated(selectedOpco);
      }
      
      setTimeout(() => onClose(), 500);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'OPCO:', error);
      showError("Erreur lors de la mise à jour de l'OPCO");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-change-opco");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  return createPortal(
    visible && (
      <div id="modal-change-opco" className={styles.modal}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose}>
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer} style={{ position: 'relative' }}>
          <Card
            className={cn(styles.card)}
            title="Modifier l'OPCO"
            classTitle="title-blue"
            style={{ minHeight: '550px', width: '600px', position: 'relative' }}
          >
            <div className={styles.description}>
              <div className={styles.field} style={{ position: 'relative', zIndex: 9999, marginBottom: '40px' }}>
                <h3 style={{ marginBottom: '20px', fontSize: '16px' }}>Veuillez sélectionner l'OPCO à assigner à ce dossier :</h3>
                
                {isLoadingOpcos ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Icon name="loader" size="24" />
                    <p>Chargement des OPCO...</p>
                  </div>
                ) : (
                  <Dropdown
                    className={styles.dropdown}
                    label="Sélectionner un OPCO"
                    name="opco"
                    type="text"
                    required
                    options={opcoOptions.map(opco => opco.label)}
                    setValue={(value) => {
                      // Trouver l'ID correspondant au nom sélectionné
                      const selectedOpcoOption = opcoOptions.find(opco => opco.label === value);
                      setSelectedOpco(selectedOpcoOption ? selectedOpcoOption.value : value);
                    }}
                    value={opcoOptions.find(opco => opco.value === selectedOpco)?.label || selectedOpco}
                    dropdownListStyles={{ position: 'absolute', zIndex: 9999, maxHeight: '300px', overflow: 'auto' }}
                  />
                )}
              </div>
              
              <div className={styles.btnWrap} style={{ marginTop: '60px' }}>
                <button 
                  className="button-blue"
                  onClick={handleSaveOpco}
                  disabled={isUpdating || !selectedOpco || selectedOpco === currentOpco}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isUpdating ? (
                    <>
                      <Icon name="download" size="16" />
                      Mise à jour en cours...
                    </>
                  ) : (
                    <>
                      <Icon name="download" size="16" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalChangeOpco;
